import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { LectureWatchProgress, userApi, User, UserDetails, UserListParams } from '../../../services/admin/userApi';
import { inquiryApi, Inquiry } from '../../../services/admin/inquiryApi';
import { getDepartments, getUniversities } from '../../../services/personApi';
import CommunityTablePagination from '../community/CommunityTablePagination';
import { AdminPage, AdminPageHeader, AdminPageTitle, AdminSection } from '../common/AdminPrimitives';
import {
  ActionButton,
  BodyCell,
  CellText,
  EmptyText,
  HeaderCell,
  HeaderText,
  RetryButton,
  TableBody,
  TableContainer,
  TableHeader,
  TableHeaderRow,
  TableInner,
  TableRow,
} from '../common/AdminTablePrimitives';
import {
  CloseButton,
  FormGroup,
  FormLabel,
  FormRow,
  InputField,
  ModalBody,
  ModalCard,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  PrimaryButton,
  ReadonlyField,
  SecondaryButton,
  SidePanelBody,
  SidePanelFooter,
  SidePanelHeader,
  SidePanelModal,
  SidePanelOverlay,
} from '../common/AdminModalPrimitives';
import AdminFilterDropdown from '../common/AdminFilterDropdown';
import { InquiryReplyModal } from '../inquiry/InquiryListComponent';

type IdentityFilter = 'STUDENT' | 'DOCTOR' | 'ADMIN' | 'OTHER' | 'all';
type ApprovalStatusFilter = 'PENDING' | 'APPROVED' | 'REJECTED' | 'all';

const UserManagementComponent: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAffiliation, setSelectedAffiliation] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedIdentity, setSelectedIdentity] = useState<IdentityFilter>('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedApprovalStatus, setSelectedApprovalStatus] = useState<ApprovalStatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [actualSearchQuery, setActualSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ page: 0, size: 10, totalPages: 0, totalElements: 0 });
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUnsuspendModal, setShowUnsuspendModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [certUserDetails, setCertUserDetails] = useState<any>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [affiliations, setAffiliations] = useState<string[]>([]);
  const [affiliationsLoading, setAffiliationsLoading] = useState(false);
  const [lectureProgress, setLectureProgress] = useState<LectureWatchProgress | null>(null);
  const [lectureProgressLoading, setLectureProgressLoading] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: UserListParams = {
        page: pagination.page,
        size: pagination.size,
        identity: selectedIdentity !== 'all' ? selectedIdentity : undefined,
        department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
        approvalStatus: selectedApprovalStatus !== 'all' ? selectedApprovalStatus : undefined,
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        affiliation: selectedAffiliation !== 'all' ? selectedAffiliation : undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        search: actualSearchQuery || undefined,
      };
      const response = await userApi.getUsers(params);
      setUsers(response.content);
      setPagination((prev) => ({ ...prev, totalPages: response.totalPages, totalElements: response.totalElements }));
    } catch {
      setError('사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [actualSearchQuery, pagination.page, pagination.size, selectedAffiliation, selectedApprovalStatus, selectedCategory, selectedDepartment, selectedIdentity, selectedStatus]);

  const fetchAffiliations = useCallback(async (identity: 'STUDENT' | 'DOCTOR' | 'all') => {
    if (identity === 'all') {
      setAffiliations([]);
      return;
    }
    try {
      setAffiliationsLoading(true);
      setAffiliations(identity === 'STUDENT' ? await getUniversities() : await getDepartments());
    } catch {
      setAffiliations([]);
    } finally {
      setAffiliationsLoading(false);
    }
  }, []);

  const handleIdentityChange = (identity: IdentityFilter) => {
    setSelectedIdentity(identity);
    setSelectedDepartment('all');
    setPagination((prev) => ({ ...prev, page: 0 }));
    if (identity === 'STUDENT' || identity === 'DOCTOR') {
      fetchAffiliations(identity);
    } else {
      setAffiliations([]);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    setActualSearchQuery(searchQuery);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleResetFilters = () => {
    setSelectedIdentity('all');
    setSelectedDepartment('all');
    setSelectedApprovalStatus('all');
    setSelectedCategory('all');
    setSelectedAffiliation('all');
    setSelectedStatus('all');
    setSearchQuery('');
    setActualSearchQuery('');
    setPagination((prev) => ({ ...prev, page: 0 }));
    setAffiliations([]);
  };

  const handleUserClick = async (user: User) => {
    try {
      const details = await userApi.getUserDetails(user.id);
      setSelectedUser(details);
      setShowUserModal(true);
    } catch {
      setError('사용자 상세 정보를 불러오는데 실패했습니다.');
    }
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setSelectedUser(null);
    setLectureProgress(null);
    setSelectedInquiry(null);
  };

  const handleSuspendUser = async () => {
    if (!selectedUser || !suspendReason.trim()) {
      alert('정지 사유를 입력해주세요.');
      return;
    }
    try {
      await userApi.toggleUserFreeze(selectedUser.id, true, suspendReason.trim());
      setShowSuspendModal(false);
      setShowUserModal(false);
      setSuspendReason('');
      fetchUsers();
      alert('계정이 정지되었습니다.');
    } catch {
      setError('계정 정지에 실패했습니다.');
    }
  };

  const handleUnsuspendUser = async () => {
    if (!selectedUser) return;
    try {
      await userApi.toggleUserFreeze(selectedUser.id, false);
      setShowUnsuspendModal(false);
      setShowUserModal(false);
      fetchUsers();
      alert('계정 정지가 해제되었습니다.');
    } catch {
      setError('계정 정지 해제에 실패했습니다.');
    }
  };

  const handlePasswordReset = async () => {
    if (!selectedUser || !newPassword.trim()) {
      alert('새 비밀번호를 입력해주세요.');
      return;
    }
    try {
      await userApi.changePassword(selectedUser.email, newPassword);
      setShowPasswordModal(false);
      setShowUserModal(false);
      setNewPassword('');
      alert('비밀번호가 변경되었습니다.');
    } catch (err: any) {
      alert(`비밀번호 변경 실패: ${err.response?.data?.message || err.message || '오류가 발생했습니다.'}`);
    }
  };

  const handleCertificateClick = async (user: UserDetails) => {
    setCertUserDetails({
      person_info: {
        person_id: user.id,
        name: user.name,
        email: user.email,
        identity: user.category === '학생' ? 'STUDENT' : user.category === '의사' ? 'DOCTOR' : user.category === '관리자' ? 'ADMIN' : user.category,
        department: user.department,
        license: user.license || null,
        id_card_file_url: user.idCardFileUrl || null,
        birth_date: user.birthDate,
        phone_number: user.phone,
        created_at: user.createdAt,
        post_count: user.postsCount,
        reply_count: user.commentsCount,
        approval_status: user.approvalStatus,
      },
    });
    setShowCertModal(true);
  };

  const handleLectureClick = async (lectureId: number) => {
    if (!selectedUser) return;
    setLectureProgressLoading(true);
    try {
      setLectureProgress(await userApi.getLectureWatchProgress(selectedUser.id, lectureId));
    } catch {
      alert('강의 시청 정보를 불러오지 못했습니다.');
    } finally {
      setLectureProgressLoading(false);
    }
  };

  const handleInquiryClick = async (inquiryId: number) => {
    try {
      setSelectedInquiry(await inquiryApi.getInquiryById(inquiryId));
    } catch {
      alert('문의 상세 정보를 불러오지 못했습니다.');
    }
  };

  const refreshSelectedUser = async () => {
    if (!selectedUser) return;
    setSelectedUser(await userApi.getUserDetails(selectedUser.id));
  };

  const handleCompleteLecture = async (lectureId: number) => {
    if (!selectedUser) return;
    const refreshed = await userApi.completeLecture(selectedUser.id, lectureId);
    setLectureProgress(refreshed);
    await refreshSelectedUser();
  };

  const handleCertApprove = async () => {
    if (!certUserDetails) return;
    try {
      await userApi.approveUser(certUserDetails.person_info.person_id);
      alert('사용자가 승인되었습니다.');
      setShowCertModal(false);
      setCertUserDetails(null);
      setShowUserModal(false);
      fetchUsers();
    } catch {
      alert('승인 처리에 실패했습니다.');
    }
  };

  const handleCertReject = () => {
    setShowCertModal(false);
    alert('반려 기능은 계정 정지를 통해 처리해주세요.');
  };

  const isInitialLoading = loading && users.length === 0;
  if (isInitialLoading) {
    return <StatePage title="회원 관리" message="로딩 중..." retry={fetchUsers} loading />;
  }
  if (error && users.length === 0) {
    return <StatePage title="회원 관리" message={error} retry={fetchUsers} />;
  }

  return (
    <AdminPage>
      <AdminPageHeader>
        <AdminPageTitle>회원 관리</AdminPageTitle>
      </AdminPageHeader>
      <AdminSection>
        <FilterRow>
          <FilterGroup>
            <AdminFilterDropdown
              value={selectedIdentity}
              onChange={(value) => handleIdentityChange(value as IdentityFilter)}
              maxWidth="140px"
              options={[
                { value: 'all', label: '구분' },
                { value: 'STUDENT', label: '학생' },
                { value: 'DOCTOR', label: '의사' },
                { value: 'ADMIN', label: '관리자' },
                { value: 'OTHER', label: '기타' },
              ]}
            />
            {selectedIdentity !== 'ADMIN' && selectedIdentity !== 'OTHER' && (
              <AffiliationFilterWrap>
                <AdminFilterDropdown
                  value={selectedDepartment}
                  onChange={setSelectedDepartment}
                  disabled={affiliationsLoading || affiliations.length === 0}
                  maxWidth="180px"
                  options={[
                    {
                      value: 'all',
                      label: affiliationsLoading
                        ? '로딩 중...'
                        : selectedIdentity === 'all'
                          ? '소속'
                          : affiliations.length === 0
                            ? '소속 정보 없음'
                            : '소속',
                    },
                    ...affiliations.map((affiliation) => ({ value: affiliation, label: affiliation })),
                  ]}
                />
                {selectedIdentity === 'all' && <AffiliationTooltip>구분을 먼저 선택해주세요.</AffiliationTooltip>}
              </AffiliationFilterWrap>
            )}
            <AdminFilterDropdown
              value={selectedApprovalStatus}
              onChange={(value) => setSelectedApprovalStatus(value as ApprovalStatusFilter)}
              maxWidth="140px"
              options={[
                { value: 'all', label: '승인 상태' },
                { value: 'PENDING', label: '승인 대기' },
                { value: 'APPROVED', label: '승인 완료' },
                { value: 'REJECTED', label: '승인 거절' },
              ]}
            />
          </FilterGroup>
          <SearchGroup>
            <SearchInput
              type="text"
              placeholder="검색어를 입력해주세요"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
            <ActionButton $tone="primary" onClick={handleSearch}>검색</ActionButton>
            <ActionButton $tone="warning" onClick={handleResetFilters}>초기화</ActionButton>
          </SearchGroup>
        </FilterRow>
        <TableContainer>
          <TableInner>
            <TableHeader>
              <TableHeaderRow $columns="60px minmax(75px, 1.5fr) minmax(60px, 1.2fr) minmax(100px, 2fr) minmax(120px, 2.5fr) 140px 130px">
                <HeaderCell><HeaderText>번호</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>이름</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>구분</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>소속</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>이메일</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>전화번호</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>관리</HeaderText></HeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {users.length > 0 ? users.map((user, index) => (
                <TableRow key={user.id} $columns="60px minmax(75px, 1.5fr) minmax(60px, 1.2fr) minmax(100px, 2fr) minmax(120px, 2.5fr) 140px 130px">
                  <BodyCell><CellText>{pagination.totalElements - pagination.page * pagination.size - index}</CellText></BodyCell>
                  <BodyCell><CellText>{user.name}</CellText></BodyCell>
                  <BodyCell><CellText>{user.category}</CellText></BodyCell>
                  <BodyCell><CellText>{user.department}</CellText></BodyCell>
                  <BodyCell><CellText>{user.email}</CellText></BodyCell>
                  <BodyCell><CellText>{user.phone}</CellText></BodyCell>
                  <BodyCell><ActionButton $tone="primary" onClick={() => handleUserClick(user)}>회원정보 관리</ActionButton></BodyCell>
                </TableRow>
              )) : <EmptyRow><EmptyText>조회된 사용자가 없습니다.</EmptyText></EmptyRow>}
            </TableBody>
          </TableInner>
          <CommunityTablePagination
            currentPage={pagination.page + 1}
            totalPages={pagination.totalPages}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, page: page - 1 }))}
          />
        </TableContainer>
      </AdminSection>
      {showUserModal && selectedUser && <UserDetailModal user={selectedUser} onClose={closeUserModal} onSuspendClick={() => setShowSuspendModal(true)} onUnsuspendClick={() => setShowUnsuspendModal(true)} onPasswordResetClick={() => setShowPasswordModal(true)} onCertificateClick={handleCertificateClick} onLectureClick={handleLectureClick} onInquiryClick={handleInquiryClick} lectureProgressLoading={lectureProgressLoading} />}
      {showSuspendModal && selectedUser && <ReasonModal title="계정 정지" buttonText="정지하기" userName={selectedUser.name} label="정지 사유" value={suspendReason} placeholder="정지 사유를 입력해주세요." onChange={setSuspendReason} onConfirm={handleSuspendUser} onCancel={() => { setShowSuspendModal(false); setSuspendReason(''); }} />}
      {showPasswordModal && selectedUser && <ReasonModal title="비밀번호 재설정" buttonText="변경하기" userName={selectedUser.name} label="새 비밀번호" value={newPassword} placeholder="새 비밀번호를 입력해주세요." onChange={setNewPassword} onConfirm={handlePasswordReset} onCancel={() => { setShowPasswordModal(false); setNewPassword(''); }} password />}
      {showUnsuspendModal && selectedUser && <ConfirmModal title="정지 해제" description={`${selectedUser.name} 회원 계정의 정지를 해제합니다.`} confirmText="해제하기" onConfirm={handleUnsuspendUser} onCancel={() => setShowUnsuspendModal(false)} />}
      {showCertModal && certUserDetails && <CertificateModal userDetails={certUserDetails} onClose={() => { setShowCertModal(false); setCertUserDetails(null); }} onApprove={handleCertApprove} onReject={handleCertReject} />}
      {lectureProgress && <LectureProgressModal progress={lectureProgress} onClose={() => setLectureProgress(null)} onComplete={handleCompleteLecture} />}
      {selectedInquiry && <InquiryReplyModal inquiry={selectedInquiry} onClose={() => setSelectedInquiry(null)} onUpdate={refreshSelectedUser} />}
    </AdminPage>
  );
};

const StatePage: React.FC<{ title: string; message: string; retry: () => void; loading?: boolean }> = ({ title, message, retry, loading }) => (
  <AdminPage>
    <AdminPageHeader><AdminPageTitle>{title}</AdminPageTitle></AdminPageHeader>
    <AdminSection>
      <StateMessage>{message}</StateMessage>
      {!loading && <RetryButton onClick={retry}>다시 시도</RetryButton>}
    </AdminSection>
  </AdminPage>
);

interface UserDetailModalProps {
  user: UserDetails;
  onClose: () => void;
  onSuspendClick: () => void;
  onUnsuspendClick: () => void;
  onPasswordResetClick: () => void;
  onCertificateClick: (user: UserDetails) => void;
  onLectureClick: (lectureId: number) => void;
  onInquiryClick: (inquiryId: number) => void;
  lectureProgressLoading: boolean;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({ user, onClose, onSuspendClick, onUnsuspendClick, onPasswordResetClick, onCertificateClick, onLectureClick, onInquiryClick, lectureProgressLoading }) => {
  const activeLectures = useMemo(() => user.enrolledLectures.filter((lecture) => lecture.status === 'ENROLLED' || lecture.status === 'IN_PROGRESS'), [user.enrolledLectures]);
  const completedLectures = useMemo(() => user.enrolledLectures.filter((lecture) => lecture.status === 'ENDED' || lecture.status === 'INTERVIEWED' || lecture.status === 'ISSUED'), [user.enrolledLectures]);
  return (
    <SidePanelOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <SidePanelModal>
        <SidePanelHeader>
          <ModalTitle>회원정보 관리</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </SidePanelHeader>
        {user.deactivateReason && <WarningBox><strong>이용 정지된 계정입니다.</strong><span>사유: {user.deactivateReason}</span></WarningBox>}
        <SidePanelBody>
          <InfoGrid>
            <InfoField label="이름" value={user.name} />
            <InfoField label="이메일" value={user.email} />
            <InfoField label="구분" value={user.category} />
            <InfoField label="소속" value={user.department} />
            <InfoField label="생년월일" value={user.birthDate ? new Date(user.birthDate).toLocaleDateString('ko-KR') : '-'} />
            <InfoField label="전화번호" value={user.phone} />
            <InfoField label="가입일" value={new Date(user.createdAt).toLocaleDateString('ko-KR')} />
            <InfoField label="최근 접속일" value={user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('ko-KR') : '-'} />
            <InfoField label="작성한 게시글 수" value={String(user.postsCount)} />
            <InfoField label="작성한 댓글 수" value={String(user.commentsCount)} />
          </InfoGrid>
          <ActionButton $tone="primary" onClick={() => onCertificateClick(user)}>인증 서류 확인</ActionButton>
          <LectureSection title="수강 중인 강의" lectures={activeLectures} onLectureClick={onLectureClick} loading={lectureProgressLoading} />
          <LectureStatusGuide>
            <strong>강의 수강 종료</strong>는 영상 진도율 100%에 도달한 상태이며, <strong>강의 수강 완료</strong>는 종료 후 설문까지 제출한 상태입니다.
          </LectureStatusGuide>
          <LectureSection title="수강 완료한 강의" lectures={completedLectures} onLectureClick={onLectureClick} loading={lectureProgressLoading} />
          <InquirySection inquiries={user.inquiries} onInquiryClick={onInquiryClick} />
        </SidePanelBody>
        <SidePanelFooter>
          <SecondaryButton onClick={onPasswordResetClick}>비밀번호 재설정</SecondaryButton>
          {user.deactivateReason === null ? <DangerButton onClick={onSuspendClick}>계정 정지</DangerButton> : <PrimaryButton onClick={onUnsuspendClick}>정지 해제</PrimaryButton>}
        </SidePanelFooter>
      </SidePanelModal>
    </SidePanelOverlay>
  );
};

const InfoField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <FormGroup>
    <FormLabel>{label}</FormLabel>
    <ReadonlyField>{value}</ReadonlyField>
  </FormGroup>
);

const formatDateTime = (value: string | null) => value ? new Date(value).toLocaleString('ko-KR') : '-';

const LectureSection: React.FC<{ title: string; lectures: UserDetails['enrolledLectures']; onLectureClick: (lectureId: number) => void; loading: boolean }> = ({ title, lectures, onLectureClick, loading }) => (
  <Section>
    <SectionTitle>{title}</SectionTitle>
    {lectures.length ? lectures.map((lecture) => (
      <LectureCard key={lecture.id} as="button" type="button" onClick={() => onLectureClick(lecture.id)} disabled={loading}>
        <LectureThumb src={lecture.thumbnailUrl || '/images/no-image-dummy.png'} alt={lecture.title} />
        <LectureContent>
          <LectureTitle>{lecture.title}</LectureTitle>
          <LectureMeta>{lecture.lecturerName} · {lecture.lecturerIntroduction}</LectureMeta>
          <LectureMeta>{lecture.status === 'ENROLLED' ? '수강 등록' : lecture.status === 'IN_PROGRESS' ? `진도율 ${Math.round(lecture.progress)}%` : lecture.status === 'ENDED' ? `강의 수강 종료 · ${formatDateTime(lecture.endedAt)}` : `강의 수강 완료 · ${formatDateTime(lecture.completedAt)}`}</LectureMeta>
        </LectureContent>
      </LectureCard>
    )) : <EmptyBlock>강의 내역이 없습니다.</EmptyBlock>}
  </Section>
);

const InquirySection: React.FC<{ inquiries: UserDetails['inquiries']; onInquiryClick: (inquiryId: number) => void }> = ({ inquiries, onInquiryClick }) => (
  <Section>
    <SectionTitle>1:1 문의</SectionTitle>
    {inquiries.length ? inquiries.map((inquiry) => (
      <InquiryCard key={inquiry.id} as="button" type="button" onClick={() => onInquiryClick(inquiry.id)}>
        <InquiryBadge $answered={inquiry.replyCount !== 0}>{inquiry.replyCount !== 0 ? '답변 완료' : '답변 대기'}</InquiryBadge>
        <LectureTitle>{inquiry.title}</LectureTitle>
        <LectureMeta>{new Date(inquiry.createdAt).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</LectureMeta>
      </InquiryCard>
    )) : <EmptyBlock>문의 내역이 없습니다.</EmptyBlock>}
  </Section>
);

const formatSeconds = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
    : `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

const COMPLETED_STATUSES: LectureWatchProgress['status'][] = ['ENDED', 'INTERVIEWED', 'ISSUED'];

const LectureProgressModal: React.FC<{ progress: LectureWatchProgress; onClose: () => void; onComplete: (lectureId: number) => Promise<void> }> = ({ progress, onClose, onComplete }) => {
  const duration = Math.max(progress.durationSeconds, 1);
  const isCompleted = COMPLETED_STATUSES.includes(progress.status);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onComplete(progress.lectureId);
      setConfirming(false);
    } catch {
      alert('수강 완료 처리에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalCard $maxWidth="720px">
        <ModalHeader><ModalTitle>시간대별 수강 여부</ModalTitle><CloseButton onClick={onClose}>×</CloseButton></ModalHeader>
        <ModalBody>
          <ProgressInfoGrid>
            <InfoField label="회원" value={progress.personName} />
            <InfoField label="강의" value={progress.lectureTitle} />
            <InfoField label="강사" value={progress.lecturerName} />
            <FormGroup>
              <FormLabel>진도율</FormLabel>
              <ProgressValueRow>
                <ProgressReadonly>{Math.round(progress.completionRate)}%</ProgressReadonly>
                {!isCompleted && (
                  <CompleteButton type="button" onClick={() => setConfirming(true)} disabled={submitting}>
                    {submitting ? '처리 중…' : '수강 완료 처리'}
                  </CompleteButton>
                )}
              </ProgressValueRow>
            </FormGroup>
          </ProgressInfoGrid>
          <WatchSummary>시청한 시간대</WatchSummary>
          <WatchTrack aria-label={`총 ${formatSeconds(progress.durationSeconds)} 중 시청 구간`}>
            {progress.watchedRanges.map((range, index) => (
              <WatchedRange
                key={`${range.startSecond}-${range.endSecond}-${index}`}
                $left={(range.startSecond / duration) * 100}
                $width={Math.max(((range.endSecond - range.startSecond) / duration) * 100, 0.5)}
                title={`${formatSeconds(range.startSecond)} - ${formatSeconds(range.endSecond)}`}
              />
            ))}
          </WatchTrack>
          <WatchAxis><span>0:00</span><span>{formatSeconds(progress.durationSeconds)}</span></WatchAxis>
          {progress.watchedRanges.length === 0 && <EmptyBlock>기록된 시청 구간이 없습니다.</EmptyBlock>}
        </ModalBody>
      </ModalCard>
      {confirming && (
        <ConfirmModal
          title="수강 완료 처리"
          description={`${progress.personName} 회원의 '${progress.lectureTitle}' 강의를 수강 완료 처리합니다. 진도율이 100%로 설정됩니다.`}
          confirmText={submitting ? '처리 중…' : '완료 처리'}
          onConfirm={handleConfirm}
          onCancel={() => !submitting && setConfirming(false)}
        />
      )}
    </ModalOverlay>
  );
};

const ReasonModal: React.FC<{ title: string; buttonText: string; userName: string; label: string; value: string; placeholder: string; onChange: (value: string) => void; onConfirm: () => void; onCancel: () => void; password?: boolean }> = ({ title, buttonText, userName, label, value, placeholder, onChange, onConfirm, onCancel, password }) => (
  <ModalOverlay>
    <ModalCard $maxWidth="420px">
      <ModalHeader><ModalTitle>{title}</ModalTitle><CloseButton onClick={onCancel}>×</CloseButton></ModalHeader>
      <ModalBody>
        <ModalText><strong>{userName}</strong> 회원에 대한 작업을 진행합니다.</ModalText>
        <FormGroup>
          <FormLabel>{label}</FormLabel>
          <InputField type={password ? 'password' : 'text'} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
        </FormGroup>
      </ModalBody>
      <ModalFooter><SecondaryButton onClick={onCancel}>취소</SecondaryButton><PrimaryButton onClick={onConfirm}>{buttonText}</PrimaryButton></ModalFooter>
    </ModalCard>
  </ModalOverlay>
);

const ConfirmModal: React.FC<{ title: string; description: string; confirmText: string; onConfirm: () => void; onCancel: () => void }> = ({ title, description, confirmText, onConfirm, onCancel }) => (
  <ModalOverlay>
    <ModalCard $maxWidth="420px">
      <ModalHeader><ModalTitle>{title}</ModalTitle><CloseButton onClick={onCancel}>×</CloseButton></ModalHeader>
      <ModalBody><ModalText>{description}</ModalText></ModalBody>
      <ModalFooter><SecondaryButton onClick={onCancel}>취소</SecondaryButton><PrimaryButton onClick={onConfirm}>{confirmText}</PrimaryButton></ModalFooter>
    </ModalCard>
  </ModalOverlay>
);

const CertificateModal: React.FC<{ userDetails: any; onClose: () => void; onApprove: () => void; onReject: () => void }> = ({ userDetails, onClose, onApprove, onReject }) => {
  const personInfo = userDetails.person_info;
  const isDoctor = personInfo.identity === 'DOCTOR' || personInfo.identity === '의사';
  const isApproved = personInfo.approval_status === 'APPROVED';
  return (
    <SidePanelOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <SidePanelModal>
        <SidePanelHeader><ModalTitle>회원 자격 인증 서류</ModalTitle><CloseButton onClick={onClose}>×</CloseButton></SidePanelHeader>
        <SidePanelBody>
          <FormRow>
            <InfoField label="이름" value={personInfo.name} />
            <InfoField label="구분" value={personInfo.identity === 'STUDENT' ? '학생' : personInfo.identity === 'DOCTOR' ? '의사' : personInfo.identity} />
            <InfoField label="소속" value={personInfo.department} />
          </FormRow>
          {!isDoctor && personInfo.id_card_file_url && <DocImage src={personInfo.id_card_file_url} alt="인증 서류" />}
          {isDoctor && (
            <>
              <InfoField label="생년월일" value={personInfo.birth_date ? personInfo.birth_date.replace(/-/g, '').slice(2) : ''} />
              <InfoField label="면허번호" value={personInfo.license || ''} />
            </>
          )}
        </SidePanelBody>
        <SidePanelFooter>
          {isApproved ? <ApprovedButton disabled>이미 승인되었습니다.</ApprovedButton> : <><SecondaryButton onClick={onReject}>반려하기</SecondaryButton><PrimaryButton onClick={onApprove}>승인하기</PrimaryButton></>}
        </SidePanelFooter>
      </SidePanelModal>
    </SidePanelOverlay>
  );
};

const FilterRow = styled.div`display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap;`;
const FilterGroup = styled.div`display:flex;align-items:flex-start;gap:8px;flex-wrap:wrap;`;
const SearchGroup = styled.div`display:flex;gap:8px;flex-wrap:wrap;`;
const SearchInput = styled.input`min-width:240px;height:36px;padding:8px 12px;border:1px solid rgba(112,115,124,.22);border-radius:12px;background:#fff;color:#171719;font-size:12px;font-family:'Pretendard Variable',sans-serif;outline:none;box-sizing:border-box;&:focus{border-color:#000;}&::placeholder{color:rgba(55,56,60,.28);font-size:12px;}`;
const StateMessage = styled.div`padding:40px;color:#666;font-size:16px;text-align:center;`;
const EmptyRow = styled.div`padding:48px 20px;display:flex;justify-content:center;`;
const AffiliationFilterWrap = styled.div`
  position: relative;
  display: inline-flex;

  &:hover > span {
    visibility: visible;
    opacity: 1;
  }
`;
const AffiliationTooltip = styled.span`
  position: absolute;
  z-index: 10;
  top: calc(100% + 8px);
  left: 50%;
  width: max-content;
  max-width: 220px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #171719;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  pointer-events: none;
  transform: translateX(-50%);
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.15s ease;
`;
const WarningBox = styled.div`display:flex;flex-direction:column;gap:6px;padding:16px;border-radius:12px;background:rgba(229,34,34,.08);border:1px solid rgba(229,34,34,.16);color:#171719;`;
const InfoGrid = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px;@media (max-width:768px){grid-template-columns:1fr;}`;
const Section = styled.section`display:flex;flex-direction:column;gap:12px;`;
const SectionTitle = styled.h3`margin:0;font-size:18px;font-weight:700;color:#171719;`;
const LectureCard = styled.div`display:flex;width:100%;gap:12px;padding:12px;border:1px solid rgba(112,115,124,.16);border-radius:12px;background:#fff;text-align:left;font:inherit;cursor:pointer;&:hover{border-color:rgba(23,23,25,.35);}&:disabled{cursor:wait;opacity:.7;}`;
const LectureThumb = styled.img`width:96px;height:72px;object-fit:cover;border-radius:8px;background:#f4f4f5;`;
const LectureContent = styled.div`display:flex;flex-direction:column;gap:4px;min-width:0;`;
const LectureTitle = styled.div`font-size:15px;font-weight:700;color:#171719;`;
const LectureMeta = styled.div`font-size:13px;color:rgba(55,56,60,.7);line-height:20px;`;
const EmptyBlock = styled.div`padding:20px;border-radius:12px;background:#f7f7f8;color:rgba(55,56,60,.7);font-size:14px;text-align:center;`;
const InquiryCard = styled.div`display:flex;width:100%;flex-direction:column;gap:8px;padding:14px 16px;border:1px solid rgba(112,115,124,.16);border-radius:12px;background:#fff;text-align:left;font:inherit;cursor:pointer;&:hover{border-color:rgba(23,23,25,.35);}`;
const InquiryBadge = styled.span<{ $answered: boolean }>`align-self:flex-start;padding:4px 10px;border-radius:999px;font-size:12px;font-weight:700;background:${({ $answered }) => $answered ? 'rgba(68,129,129,.12)' : 'rgba(245,158,11,.12)'};color:${({ $answered }) => $answered ? '#2f6d6d' : '#b46a05'};`;
const ModalText = styled.p`margin:0 0 20px;color:#171719;font-size:16px;line-height:24px;`;
const DangerButton = styled(PrimaryButton)`background:#d14343;&:hover:not(:disabled){background:#b83838;}`;
const ApprovedButton = styled(PrimaryButton)`background:#b8bbc2;color:#fff;cursor:not-allowed;&:hover{background:#b8bbc2;}`;
const DocImage = styled.img`width:100%;border-radius:12px;border:1px solid rgba(112,115,124,.16);object-fit:contain;background:#f7f7f8;`;
const LectureStatusGuide = styled.div`padding:12px 14px;border-radius:10px;background:#f7f7f8;color:rgba(55,56,60,.78);font-size:13px;line-height:20px;`;
const ProgressInfoGrid = styled(InfoGrid)`margin-bottom:28px;`;
const ProgressValueRow = styled.div`display:flex;align-items:center;gap:8px;`;
const ProgressReadonly = styled(ReadonlyField)`flex:1;min-width:0;`;
const CompleteButton = styled.button`flex-shrink:0;padding:8px 14px;border:none;border-radius:8px;background:transparent;color:#2f6d6d;font-size:13px;font-weight:700;font-family:inherit;white-space:nowrap;cursor:pointer;&:hover:not(:disabled){background:rgba(68,129,129,.12);}&:disabled{color:rgba(55,56,60,.4);cursor:wait;}`;
const WatchSummary = styled.div`margin-bottom:12px;font-size:15px;font-weight:700;color:#171719;`;
const WatchTrack = styled.div`position:relative;height:28px;border-radius:8px;background:#e7e8eb;overflow:hidden;`;
const WatchedRange = styled.div<{ $left: number; $width: number }>`position:absolute;top:0;bottom:0;left:${({ $left }) => $left}%;width:${({ $width }) => $width}%;background:#448181;`;
const WatchAxis = styled.div`display:flex;justify-content:space-between;margin-top:8px;margin-bottom:20px;color:rgba(55,56,60,.62);font-size:12px;`;

export default UserManagementComponent;
